import { InformationCircleIcon, Square2StackIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { ReactNode, useCallback, useEffect, useId, useRef, useState } from 'react';

import { Button } from '@/components/core/Button';
import { ButtonPanel } from '@/components/core/ButtonPanel';
import { Icon } from '@/components/core/Icon';
import { Popover, PopoverContent, PopoverPortal, PopoverTrigger } from '@/components/core/Popover';
import { useTranslation } from '@/hooks/useTranslation';

interface StatusMessagePopoverProps {
    badge: ReactNode;
    message: string;
    header?: ReactNode;
    ariaLabel: string;
    initiallyOpen?: boolean;
    /** Return false to keep the popover closed, for example when details are still loading. */
    prepareOpen?: () => Promise<boolean>;
    onShowReport: () => void;
}

const navbarCollisionPadding = () => {
    const navbar = typeof document === 'undefined'
        ? null
        : document.getElementById('top-navigation');

    return {
        top: Math.ceil(navbar?.getBoundingClientRect().bottom ?? 0) + 8,
        right: 8,
        bottom: 8,
        left: 8,
    };
};

const StatusMessagePopover = ({
    badge,
    message,
    header,
    ariaLabel,
    initiallyOpen = false,
    prepareOpen,
    onShowReport,
}: StatusMessagePopoverProps) => {
    const { t } = useTranslation();
    const popoverId = useId();
    const opening = useRef(false);
    const skipNextFocus = useRef(false);
    const skipTimer = useRef<number | undefined>(undefined);
    const [open, setOpen] = useState(initiallyOpen);
    const [truncated, setTruncated] = useState(false);
    const copyLabel = t('PUBLICATION_ERROR.COPY');

    useEffect(() => () => window.clearTimeout(skipTimer.current), []);

    const measureMessage = useCallback((element: HTMLParagraphElement | null) => {
        if (!element || message.length === 0) {
            setTruncated(false);
            return;
        }
        setTruncated(element.scrollHeight > element.clientHeight + 1);
    }, [message]);

    const armSkipFocus = () => {
        skipNextFocus.current = true;
        window.clearTimeout(skipTimer.current);
        skipTimer.current = window.setTimeout(() => {
            skipNextFocus.current = false;
        }, 0);
    };

    const requestOpen = async () => {
        if (open || opening.current) {
            return;
        }
        if (prepareOpen) {
            opening.current = true;
            const ready = await prepareOpen();
            opening.current = false;
            if (!ready) {
                return;
            }
        }
        setOpen(true);
    };

    const closePopover = () => {
        armSkipFocus();
        setOpen(false);
    };

    const showReport = () => {
        closePopover();
        onShowReport();
    };

    return (
        <Popover
            open={open}
            onOpenChange={(next) => {
                if (next) {
                    void requestOpen();
                    return;
                }
                closePopover();
            }}
        >
            <PopoverTrigger asChild>
                <button
                    type="button"
                    className="tw-inline-flex tw-w-max tw-max-w-none tw-shrink-0 tw-items-center tw-gap-1 tw-whitespace-nowrap tw-border-0 tw-bg-transparent
                        tw-p-0 tw-cursor-pointer focus-visible:tw-rounded focus-visible:tw-outline-none focus-visible:tw-border-solid focus-visible:tw-border-2 focus-visible:tw-border-primary-focus"
                    aria-label={ariaLabel}
                    aria-haspopup="dialog"
                    aria-controls={popoverId}
                    onFocus={(event) => {
                        if (skipNextFocus.current || !event.currentTarget.matches(':focus-visible')) {
                            return;
                        }
                        void requestOpen();
                    }}
                >
                    {badge}
                    <span aria-hidden="true" className="tw-inline-flex">
                        <Icon
                            icon={InformationCircleIcon}
                            className="tw-icon-primary tw-h-5 tw-w-5 lg:tw-h-6 lg:tw-w-6"
                        />
                    </span>
                </button>
            </PopoverTrigger>
            <PopoverPortal>
                <PopoverContent
                    id={popoverId}
                    side="top"
                    align="center"
                    sideOffset={4}
                    collisionPadding={navbarCollisionPadding()}
                    hasPadding={false}
                    className="tw-w-80 lg:tw-w-[28rem] lg:tw-max-w-md"
                    onOpenAutoFocus={(event) => event.preventDefault()}
                >
                    <div className="tw-flex tw-w-full tw-flex-col tw-items-start tw-gap-4 tw-px-5 tw-pb-5 tw-pt-3 lg:tw-px-6 lg:tw-pb-6 lg:tw-pt-4">
                        <div className="tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-3">
                            {header}
                            <div className="tw-ml-auto tw-flex tw-shrink-0 tw-items-center tw-gap-3">
                                <ButtonPanel
                                    isDisabled={false}
                                    icon={Square2StackIcon}
                                    tooltip={copyLabel}
                                    aria-label={copyLabel}
                                    onClick={() => {
                                        void navigator.clipboard.writeText(message);
                                    }}
                                />
                                <ButtonPanel
                                    isDisabled={false}
                                    icon={XMarkIcon}
                                    tooltip={t('CLOSE')}
                                    aria-label={t('CLOSE')}
                                    onClick={closePopover}
                                />
                            </div>
                        </div>
                        <p
                            ref={measureMessage}
                            className="tw-m-0 tw-w-full tw-overflow-hidden tw-whitespace-pre-wrap tw-break-words tw-text-t-sm tw-font-normal tw-text-t-primary tw-line-clamp-3 lg:tw-text-t-base"
                        >
                            {message}
                        </p>
                        {truncated && (
                            <Button variant="outline" onClick={showReport}>
                                {t('PUBLICATION_ERROR.SHOW_FULL_REPORT')}
                            </Button>
                        )}
                    </div>
                </PopoverContent>
            </PopoverPortal>
        </Popover>
    );
};

StatusMessagePopover.displayName = 'StatusMessagePopover';

export { StatusMessagePopover };
